<template>
  <div id="cesium-container" ref="cesiumContainer">
    <div id="cesium-color-bar-wrapper" ref="colorBar">
      <div
        v-for="(tickLegend, index) in heatMapLegend"
        :key="tickLegend"
        :style="`top:calc(${
          (100 / (heatMapLegend.length - 1)) * parseInt(index)
        }% - ${100 / heatMapCommons.currentGradient.length / 2}%);`"
        class="cesium-color-bar-tick"
      >
        {{ tickLegend }}
      </div>
    </div>
    <cesium-display-options ref="cesiumDisplayOptionsList">
      <v-checkbox
        class="ma-0 pa-0"
        v-for="(option, index) in displayOptions"
        :key="index"
        :label="option.label"
        v-model="option.show"
        :dark="true"
        color="info"
        hide-details
        slot="displayOptions"
      />
    </cesium-display-options>
    <cesium-recorder ref="cesiumRecorder" />
    <table id="meshInfo" ref="meshInfo" v-show="value">
      <tr>
        <th>Longitude</th>
        <th>Latitude</th>
        <th>Average</th>
      </tr>
      <tr>
        <td>{{ longitude }}&deg;</td>
        <td>{{ latitude }}&deg;</td>
        <td>{{ value }}</td>
      </tr>
    </table>
  </div>
</template>

<script>
import __ from 'lodash'
import { CesiumInitialization } from '../../components/ThreeD/CesiumInitialization'
import { GroundStation } from '../../components/ThreeD/GroundStation'
import { AnalyticalOrbit } from '../../components/ThreeD/AnalyticalOrbit'
import CesiumRecorder from '../../components/ThreeD/CesiumRecorder.vue'
import CesiumDisplayOptionsList from '@/components/ThreeD/CesiumDisplayOptionsList.vue'
import constellationUtils from '../../mixins/constellationUtils'
import dataTools from '../../tools/data.js'
import * as Cesium from 'cesium'
import CesiumTools from '../../components/ThreeD/CesiumTools'

export default {
  name: 'analyticalDashboard-cesium-viewer',
  mixins: [
    CesiumInitialization,
    constellationUtils,
    AnalyticalOrbit,
    GroundStation
  ],
  props: [
    'currentData',
    'heatMapCommons',
    'resultsLog',
    'displaySinglePointResults',
    'displayGroundStationTimelineFrom3DView'
  ],
  components: {
    'cesium-recorder': CesiumRecorder,
    'cesium-display-options': CesiumDisplayOptionsList
  },
  data() {
    return {
      currentNumberOfLatitudes: 0,
      colorsArray: [],
      movingEntity: null,
      coloredPointsPC: null,
      textureRectanglesPC: null,
      geometries: [],
      longitude: '',
      latitude: '',
      value: undefined,
      handler: null,
      legendIsPinned: false,
      pickedLongitude: 0,
      pickedLatitude: 0,
      groundStationsNotEmpty: false,
      displayOptions: {
        groundStations: {
          show: true,
          label: 'Show ground stations'
        },
        traces: {
          show: true,
          label: 'Show traces'
        },
        points: {
          show: false,
          label: 'Show points'
        },
        rectangles: {
          show: true,
          label: 'Show rectangles'
        }
      }
    }
  },
  computed: {
    heatMapLegend() {
      if (this.heatMapCommons.tickText && this.heatMapCommons.tickText.length) {
        const newArray = this.heatMapCommons.tickText.slice().reverse()
        return newArray
      } else {
        return []
      }
    }
  },
  watch: {
    currentData: {
      handler() {
        if (this.currentData && this.heatMapCommons) {
          CesiumTools.removePrimitivesFromPC(this.textureRectanglesPC)
          CesiumTools.removePrimitivesFromPC(this.orbitTracesPC)
          for (const meshData of this.currentData) {
            let xValues = meshData.x
            let yValues = meshData.y
            let zValues = meshData.z
            let data = []
            xValues.forEach((__, x) => {
              yValues.forEach((__, y) => {
                if (typeof zValues[y][x] !== 'undefined') {
                  data.push({
                    x: xValues[x],
                    y: yValues[y],
                    value: zValues[y][x],
                    ix: x,
                    iy: y
                  })
                }
              })
            })
            this.bounds = {
              west: Math.min(...xValues),
              south: Math.min(...yValues),
              east: Math.max(...xValues),
              north: Math.max(...yValues)
            }
            this.data = data
            this.currentNumberOfLatitudes = yValues.length
            this.createColoredRectanglePrimitives()
          }
          this.addGroundStations(
            [this.resultsLog.results.homogeneousConstellationGeometries[0]],
            this.resultsLog.results.groundStations
          )
          this.addAnalyticalTraces(
            [this.resultsLog.results.ephemeridesTraces],
            {
              colorsArray: this.colorsArray
            }
          )
          this.viewer.scene.requestRender()
        }
      },
      deep: true
    },
    heatMapCommons: {
      handler() {
        this.updateColorBar()
      },
      deep: true
    },
    resultsLog: {
      handler() {
        this.groundStationsNotEmpty =
          this.resultsLog.results.groundStations &&
          this.resultsLog.results.groundStations.length
        CesiumTools.removePrimitivesFromPC(this.groundStationsPC)
        this.addGroundStations(
          [this.resultsLog.results.homogeneousConstellationGeometries[0]],
          this.resultsLog.results.groundStations
        )
      },
      deep: true
    },
    'displayOptions.groundStations.show': {
      handler(show) {
        this.groundStationsPC.show = show
        this.viewer.scene.requestRender()
      }
    },
    'displayOptions.traces.show': {
      handler(show) {
        let nbOfTraces = this.resultsLog.results.ephemeridesTraces.length
        for (let i = 0; i < nbOfTraces; i++) {
          this.orbitTracesPC.get(i).show = show
        }
        this.viewer.scene.requestRender()
      }
    },
    'displayOptions.points.show': {
      handler(show) {
        for (let i = 0; i < this.coloredPointsPC.length; i++) {
          this.coloredPointsPC.get(i).show = show
        }
        this.viewer.scene.requestRender()
      }
    },
    'displayOptions.rectangles.show': {
      handler(show) {
        this.textureRectanglesPC.show = show
        this.viewer.scene.requestRender()
      }
    }
  },

  async mounted() {
    this.initializeViewer(true, false, true, true, true)
    this.colorsArray = CesiumTools.createColorsArray(
      this.resultsLog.results.ephemeridesTraces.length
    )
    this.cameraShouldFly = false
    const defaultRectangle = Cesium.Rectangle.fromDegrees(-180, -90, 180, 90)
    Cesium.Camera.DEFAULT_VIEW_RECTANGLE = defaultRectangle
    this.viewer.camera.setView({
      destination: defaultRectangle
    })

    this.geometries = this.resultsLog.results.homogeneousConstellationGeometries
    this.geometries.sort((a, b) => (a.displayedName < b.displayedName ? -1 : 1))

    this.$refs.cesiumDisplayOptionsList.insertDisplayOptionsInToolbar(
      this.$refs.cesiumContainer
    )
    this.insertElementInToolbar(this.$refs.cesiumRecorder.$el)
    // this.insertElementInToolbar(this.$refs.displayOptionsCheckboxes)
    this.insertElementInToolbar(this.$refs.meshInfo)
    this.deactivateControls()
    this.updateColorBar()

    this.initPrimitiveCollections()
    this.setInputHandlers()
    this.viewer.scene.mode = Cesium.SceneMode.SCENE2D
    this.viewer.scene.postUpdate.addEventListener(this.updateSelectorPosition)
    this.cesiumSelector = this.$refs.cesiumContainer.querySelector(
      '.cesium-selection-wrapper'
    )
    this.viewer.scene.requestRender()
  },

  methods: {
    initPrimitiveCollections() {
      this.textureRectanglesPC = new Cesium.PrimitiveCollection()
      this.coloredPointsPC = new Cesium.PointPrimitiveCollection()

      this.viewer.scene.primitives.add(this.textureRectanglesPC)
      this.viewer.scene.primitives.add(this.coloredPointsPC)
      this.viewer.scene.primitives.raiseToTop(this.textureRectanglesPC)

      this.initTracesPC()
      this.initGroundStationsPC()
    },

    setInputHandlers() {
      // create handler
      this.handler = new Cesium.ScreenSpaceEventHandler(
        this.viewer.scene.canvas
      )
      // updates values of the table
      this.handler.setInputAction(
        this.onMouseMove,
        Cesium.ScreenSpaceEventType.MOUSE_MOVE
      )
      // disable updating when user pans or zooms map
      this.handler.setInputAction(
        this.onLeftClickDown,
        Cesium.ScreenSpaceEventType.LEFT_DOWN
      )
      this.handler.setInputAction(
        this.onLeftClickUp,
        Cesium.ScreenSpaceEventType.LEFT_UP
      )
      this.handler.setInputAction(
        this.onLeftClickDown,
        Cesium.ScreenSpaceEventType.RIGHT_DOWN
      )
      this.handler.setInputAction(
        this.onLeftClickUp,
        Cesium.ScreenSpaceEventType.RIGHT_UP
      )
      // pins legend table
      this.handler.setInputAction(
        this.onClick,
        Cesium.ScreenSpaceEventType.LEFT_CLICK
      )
      // unpins legend table, selects mesh point for single point results
      this.handler.setInputAction(
        this.onRightClick,
        Cesium.ScreenSpaceEventType.RIGHT_CLICK
      )
      // selects ground station for timeline
      this.handler.setInputAction(
        this.onMiddleClick,
        Cesium.ScreenSpaceEventType.MIDDLE_CLICK
      )
    },

    updateSelectorPosition() {
      if (
        !this.legendIsPinned ||
        !this.pickedLongitude ||
        !this.pickedLatitude
      ) {
        return
      }
      const screenPosition =
        this.viewer.selectionIndicator.viewModel.computeScreenSpacePosition(
          Cesium.Cartesian3.fromDegrees(
            this.pickedLongitude,
            this.pickedLatitude,
            0
          )
        )
      this.cesiumSelector.style.top = `calc(${screenPosition.y}px - 80px)`
      this.cesiumSelector.style.left = `calc(${screenPosition.x}px - 80px)`
    },

    onLeftClickDown() {
      this.handler.removeInputAction(Cesium.ScreenSpaceEventType.MOUSE_MOVE)
    },

    onLeftClickUp() {
      this.handler.setInputAction(
        this.onMouseMove,
        Cesium.ScreenSpaceEventType.MOUSE_MOVE
      )
    },

    onClick(clickPos) {
      let pickedPrimitive = this.viewer.scene.pick(clickPos.position)
      this.updateSinglePointResults(clickPos)
      if (
        Cesium.defined(pickedPrimitive) &&
        typeof pickedPrimitive.id === 'string'
      ) {
        this.pinLegend(pickedPrimitive)
      } else {
        this.unPinLegend()
      }
    },

    onRightClick() {
      this.unPinLegend()
    },

    onMiddleClick(click) {
      const pickedFeature = this.viewer.scene.pick(click.position)
      if (
        Cesium.defined(pickedFeature) &&
        typeof pickedFeature.id === 'string'
      ) {
        const objectName = pickedFeature.id
        this.displayGroundStationTimelineFrom3DView(objectName)
      } // else Globe was not picked
    },

    pinLegend(pickedPrimitive) {
      this.legendIsPinned = true
      this.handler.removeInputAction(Cesium.ScreenSpaceEventType.MOUSE_MOVE)
      const featureValues = pickedPrimitive.id.split(';')
      this.pickedLongitude = parseFloat(featureValues[0])
      this.pickedLatitude = parseFloat(featureValues[1])
      this.updateSelectorPosition()
      this.cesiumSelector.classList.add('cesium-selection-wrapper-visible')
      this.viewer.selectionIndicator.viewModel.animateAppear()
    },

    unPinLegend() {
      this.handler.setInputAction(
        this.onMouseMove,
        Cesium.ScreenSpaceEventType.MOUSE_MOVE
      )
      this.viewer.selectionIndicator.viewModel.animateDepart()
      this.cesiumSelector.classList.remove('cesium-selection-wrapper-visible')
    },

    updateSinglePointResults(click) {
      const cartesianPos = this.viewer.camera.pickEllipsoid(
        click.position,
        this.viewer.scene.globe.ellipsoid
      )
      if (Cesium.defined(cartesianPos)) {
        const cartoPos = Cesium.Cartographic.fromCartesian(cartesianPos)
        this.displaySinglePointResults(cartoPos.latitude, cartoPos.longitude)
      }
    },

    onMouseMove(movement) {
      const pickedFeature = this.viewer.scene.pick(movement.endPosition)
      if (
        Cesium.defined(pickedFeature) &&
        typeof pickedFeature.id === 'string'
      ) {
        const featureValues = pickedFeature.id.split(';')
        this.longitude = parseFloat(featureValues[0]).toFixed(4)
        this.latitude = parseFloat(featureValues[1]).toFixed(4)
        this.value = featureValues[2]
      } else {
        this.value = undefined
      }
    },

    createColoredRectanglePrimitives() {
      let rectangleInstances = []
      let [height, width] = this.computeRectangleDimensionsInDegrees(this.data)
      let isLatitudeStepConstant = this.isLatitudeStepConstant(this.data)
      let listOfCenterLatitudes
      let listOfHeights
      if (isLatitudeStepConstant === false) {
        ;[listOfCenterLatitudes, listOfHeights] =
          this.computeParametersPerLatitude(this.data)
      }
      // const boundingRectangle = this.computeBoundingRectangle(this.data)
      this.data.forEach((point, index) => {
        const color = this.computeRectangleColor(point, index)
        let centerPoint
        if (isLatitudeStepConstant === true) {
          centerPoint = { lat: point.y, lon: point.x, value: point.value }
        } else {
          centerPoint = {
            lat: listOfCenterLatitudes[point.iy],
            lon: point.x,
            value: point.value
          }
          height = listOfHeights[point.iy]
        }
        rectangleInstances.push(
          this.createRectangleInstanceFromCenterDimensionsColor(
            centerPoint,
            height,
            width,
            color
          )
        )
        this.coloredPointsPC.add({
          position: Cesium.Cartesian3.fromDegrees(point.x, point.y),
          pixelSize: 4,
          color: color,
          show: this.displayOptions.points.show,
          id: `${point.x};${point.y};${this.formatSecondsToDHMS(
            point.value
          )};${color.toString()};`
        })
      })
      let rectanglesPrimitive = new Cesium.GroundPrimitive({
        geometryInstances: rectangleInstances,
        allowPicking: true,
        appearance: new Cesium.PerInstanceColorAppearance({
          flat: true,
          translucent: true
        })
      })
      this.textureRectanglesPC.add(rectanglesPrimitive)
      /*       this.viewer.camera.setView({
        destination: boundingRectangle
      })
      this.viewer.camera.moveBackward(
        Math.max(
          Cesium.Rectangle.computeWidth(boundingRectangle),
          Cesium.Rectangle.computeHeight(boundingRectangle)
        ) * 3e6
      ) */
      this.viewer.camera.setView({
        destination: Cesium.Camera.DEFAULT_VIEW_RECTANGLE
      })
    },

    createRectangleInstanceFromCenterDimensionsColor(
      center,
      height,
      width,
      color
    ) {
      const [w, s, e, n] = this.getRectangleCoordinatesFromCenterDimensions(
        center,
        height,
        width
      )
      const rectangle = new Cesium.Rectangle(w, s, e, n)
      return new Cesium.GeometryInstance({
        id: `${center.lon};${center.lat};${this.formatSecondsToDHMS(
          center.value
        )};${color.toString()};`,
        geometry: new Cesium.RectangleGeometry({
          rectangle: rectangle,
          vertexFormat: Cesium.EllipsoidSurfaceAppearance.VERTEX_FORMAT,
          granularity: Cesium.Math.RADIANS_PER_DEGREE * 6,
          extrudedHeight: 0
        }),
        attributes: {
          color: Cesium.ColorGeometryInstanceAttribute.fromColor(color),
          show: new Cesium.ShowGeometryInstanceAttribute(true)
        }
      })
    },

    getRectangleCoordinatesFromCenterDimensions(center, height, width) {
      const west = CesiumTools.clampLongitude(
        CesiumTools.toRadians(center.lon) - CesiumTools.toRadians(width / 2)
      )
      const south = CesiumTools.clampLatitude(
        CesiumTools.toRadians(center.lat) - CesiumTools.toRadians(height / 2)
      )
      const east = CesiumTools.clampLongitude(
        CesiumTools.toRadians(center.lon) + CesiumTools.toRadians(width / 2)
      )
      const north = CesiumTools.clampLatitude(
        CesiumTools.toRadians(center.lat) + CesiumTools.toRadians(height / 2)
      )
      return [west, south, east, north]
    },

    computeRectangleColor(point) {
      let gradient = this.heatMapCommons.currentGradient
      let mappedValue = constellationUtils.scale(
        point.value,
        this.heatMapCommons.zMin,
        this.heatMapCommons.zMax,
        0,
        gradient.length - 1
      )
      let color1 = constellationUtils.hexToRgb(
        gradient[Math.floor(mappedValue)]
      )
      let color2 = constellationUtils.hexToRgb(gradient[Math.ceil(mappedValue)])
      let w2 = mappedValue % 1
      let w1 = 1 - w2
      const mixColor = (color1, color2, weight1, weight2) => {
        return [
          (color1.r / 255) * weight1 + (color2.r / 255) * weight2,
          (color1.g / 255) * weight1 + (color2.g / 255) * weight2,
          (color1.b / 255) * weight1 + (color2.b / 255) * weight2
        ]
      }

      const [r3, g3, b3] = mixColor(color1, color2, w1, w2)

      return new Cesium.Color(r3, g3, b3).withAlpha(0.6)
    },

    computeBoundingRectangle(pointsDegrees) {
      const maxIx = pointsDegrees.reduce(
        (max, currentValue) => (max.ix > currentValue.ix ? max : currentValue),
        pointsDegrees[0]
      ).ix
      const maxIy = pointsDegrees.reduce(
        (max, currentValue) => (max.iy > currentValue.iy ? max : currentValue),
        pointsDegrees[0]
      ).iy
      let point = pointsDegrees.find(
        (point) => point.ix === 0 && point.iy === 0
      )
      let south = point.y
      let west = point.x

      point = pointsDegrees.find(
        (point) => point.ix === maxIx && point.iy === maxIy
      )
      let north = point.y
      let east = point.x

      return Cesium.Rectangle.fromDegrees(west, south, east, north)
    },

    isLatitudeStepConstant(pointsDegrees) {
      let firstPoint = pointsDegrees[0]
      let secondPoint = pointsDegrees.find((point) => point.iy === 1)
      let thirdPoint = pointsDegrees.find((point) => point.iy === 2)

      return secondPoint.y - firstPoint.y == thirdPoint.y - secondPoint.y
    },

    computeRectangleDimensionsInDegrees(pointsDegrees) {
      let firstPoint = pointsDegrees[0]
      let secondPoint = pointsDegrees.find(
        (point) => point.ix === 1 && point.iy === 1
      )
      let width = Math.abs(secondPoint.x - firstPoint.x)
      let height = Math.abs(secondPoint.y - firstPoint.y)
      return [height, width]
    },

    computeParametersPerLatitude(pointsDegrees) {
      let listOfCenterLatitudes = []
      let listOfHeights = []

      let currentLatStep = Math.abs(pointsDegrees[1].y - pointsDegrees[0].y)

      for (let iLat = 0; iLat < this.currentNumberOfLatitudes; iLat++) {
        let currentLatIndex = iLat
        let nextLatIndex = 1 + iLat

        let previousLatStep = currentLatStep
        if (iLat == this.currentNumberOfLatitudes - 1) {
          currentLatStep = previousLatStep
        } else {
          currentLatStep = Math.abs(
            pointsDegrees[nextLatIndex].y - pointsDegrees[currentLatIndex].y
          )
        }

        let center =
          (currentLatStep - previousLatStep) / 4 +
          pointsDegrees[currentLatIndex].y
        let height = (currentLatStep + previousLatStep) / 2

        listOfCenterLatitudes.push(center)
        listOfHeights.push(height)
      }

      return [listOfCenterLatitudes, listOfHeights]
    },

    formatSecondsToDHMS(value) {
      return dataTools.sec2DDHHMMSS(value)
    },

    updateColorBar() {
      let colorBar = this.$refs.colorBar
      let colorsString = [...this.heatMapCommons.currentGradient]
        .reverse()
        .toString()
      colorBar.style.background = `linear-gradient(${colorsString})`
    }
  }
}
</script>

<style scoped>
#cesium-container {
  position: relative;
  width: 100%;
  height: calc(100% - 0px);
}

#cesium-color-bar-wrapper {
  position: absolute;
  z-index: 3;
  top: 3%;
  height: 94%;
  left: 1%;
  width: 5%;
  max-width: 100px;
}

#cesium-color-bar {
  position: relative;
  height: 100%;
}

.cesium-color-bar-tick {
  position: absolute;
  left: calc(100% + 10px);
  font-size: 0.8em;
  height: 0.8em;
  line-height: 0.8em;
  color: white !important;
  display: block;
}

.cesium-color-bar-tick::after {
  display: block;
  content: '';
  top: calc(50% - 1px);
  left: -10px;
  position: absolute;
  width: 10px;
  height: 1px;
  border-top: 1px solid white;
}

#meshInfo {
  background-color: #303336;
  border: 1px solid #444;
  color: white;
  display: inline-table;
  z-index: 3;
  font-size: 0.7em;
  margin: 2px 3px;
}
#meshInfo {
  border-collapse: collapse;
}
#meshInfo tr th,
#meshInfo tr td {
  border: 1px solid #444;
  padding: 5px;
}
#meshInfo tr td {
  text-align: right;
  min-width: 10px;
}
</style>
